import { parsePhoneNumber, isValidPhoneNumber, getCountryCallingCode, AsYouType, Metadata } from "libphonenumber-js";

/**
 * Validates a phone number, optionally with a default country code.
 * @param {string} phone
 * @param {string} defaultCountry - ISO 3166 country code (e.g., "US", "BR")
 * @returns {boolean}
 */
export function isValidPhone(phone, defaultCountry = null) {
  if (!phone || typeof phone !== "string") return false;
  const trimmed = phone.trim();
  if (defaultCountry && isValidPhoneNumber(trimmed, defaultCountry)) return true;
  return isValidPhoneNumber(trimmed);
}

/**
 * Formats a phone number to international display format (e.g., +1 212 555 1234).
 * @param {string} phone
 * @param {string} defaultCountry
 * @returns {string|null}
 */
export function formatPhoneInternational(phone, defaultCountry = null) {
  try {
    if (!phone || typeof phone !== "string") return null;
    const trimmed = phone.trim();
    if (defaultCountry && isValidPhoneNumber(trimmed, defaultCountry)) {
      return parsePhoneNumber(trimmed, defaultCountry).formatInternational();
    }
    if (isValidPhoneNumber(trimmed)) {
      return parsePhoneNumber(trimmed).formatInternational();
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Parses a phone number and returns structured info.
 * @param {string} phone
 * @param {string} defaultCountry
 * @returns {object|null} - { number, country, nationalNumber, countryCallingCode, isValid, formatted }
 */
export function parsePhone(phone, defaultCountry = null) {
  try {
    if (!phone || typeof phone !== "string") return null;
    const trimmed = phone.trim();

    let parsed = null;
    if (defaultCountry && isValidPhoneNumber(trimmed, defaultCountry)) {
      parsed = parsePhoneNumber(trimmed, defaultCountry);
    } else if (isValidPhoneNumber(trimmed)) {
      parsed = parsePhoneNumber(trimmed);
    } else if (!trimmed.startsWith("+")) {
      const withPlus = "+" + trimmed;
      if (isValidPhoneNumber(withPlus)) {
        parsed = parsePhoneNumber(withPlus);
      }
    }

    if (!parsed) return null;

    return {
      number: parsed.number,
      country: parsed.country,
      nationalNumber: parsed.nationalNumber,
      countryCallingCode: parsed.countryCallingCode,
      isValid: true,
      formatted: parsed.formatInternational(),
    };
  } catch {
    return null;
  }
}

/**
 * Normalizes a phone number for storage. Tries to format to E.164.
 * If valid, returns the E.164 format. If not, returns cleaned input.
 * @param {string} phone
 * @param {string} defaultCountry
 * @returns {{ phone: string|null, isValidated: boolean }}
 */
/**
 * Gets the calling code for a country (e.g., "US" → "1", "BR" → "55").
 * @param {string} countryCode - ISO 3166 country code
 * @returns {string|null}
 */
export function getCallingCode(countryCode) {
  try {
    if (!countryCode) return null;
    return getCountryCallingCode(countryCode.toUpperCase());
  } catch {
    return null;
  }
}

export function normalizePhone(phone, defaultCountry = null) {
  if (!phone || typeof phone !== "string") return { phone: null, isValidated: false };
  const trimmed = phone.trim();
  if (!trimmed) return { phone: null, isValidated: false };

  const parsed = parsePhone(trimmed, defaultCountry);
  if (parsed) {
    return { phone: parsed.number, isValidated: true };
  }
  return { phone: trimmed, isValidated: false };
}

/**
 * Country of a number that is still being typed. parsePhone only answers once
 * the whole number is valid, which is too late to show a flag - this answers
 * as soon as the calling code is unambiguous (e.g. "+51" -> PE).
 * @param {string} phone
 * @returns {string|null} ISO 3166 country code
 */
export function countryFromPartial(phone) {
  try {
    if (!phone || typeof phone !== "string") return null;
    const typing = new AsYouType();
    typing.input(phone.trim());
    //Exact once enough digits have been typed to tell countries apart, e.g.
    //a Canadian area code on +1.
    if (typing.country) return typing.country;
    //Codes shared by several countries (+1 by 25 of them, +44 by 4) never
    //resolve on their own. Fall back to the main country for the code -
    //getCountryCodesForCallingCode lists it first.
    const callingCode = typing.getCallingCode && typing.getCallingCode();
    if (!callingCode) return null;
    const owners = new Metadata().getCountryCodesForCallingCode(callingCode);
    return owners && owners.length ? owners[0] : null;
  } catch {
    return null;
  }
}
